import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgStorage } from '@core/enums/ngStorage/ngStorage.enum';
import { User } from '@core/interface/users/loginResponse';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { StorageService } from '../ngStorage/storage.service';

export enum CtaState {
  Login = 'login',
}

@Injectable({ providedIn: 'root' })
export class UserSessionService {
  isSessionInvalidated = false;

  private userSubject = new BehaviorSubject<User | null>(null);
  user$: Observable<User | null> = this.userSubject.asObservable();

  ctaState$: Observable<CtaState> = this.user$.pipe(
    map((user) => {
      if (!user) return CtaState.Login;
      return CtaState.Login;
    }),
  );
  private storage = inject(StorageService);
  private router = inject(Router);
  constructor() {
    // Inicializa el usuario desde storage
    this.loadUser();
    // Suscríbete a cambios en el storage
    this.storage.watchStorage(NgStorage.USER).subscribe((user) => {
      this.userSubject.next(user as User);
    });
  }

  private async loadUser() {
    const user = (await this.storage.getStorage(NgStorage.USER)) as User;
    this.userSubject.next(user);
  }

  async closeSession() {
    this.userSubject.next(null);
    await this.router.navigate(['/login']);
  }

  async updateUserImage(imageUrl: string | null): Promise<void> {
    const current = this.userSubject.value;
    if (!current) return;
    const updated = { ...current, image: imageUrl };
    await this.storage.setStorage(NgStorage.USER, updated);
    // watchStorage fires automatically → userSubject.next(updated) → user$ emits → header updates
  }

  // Helpers para los botones

  /**
   * Observable that emits true when the user is not logged in (CTA state is 'login') and false otherwise.
   * @returns {Observable<boolean>} - Observable that emits true when the user is not logged in and false otherwise.
   */
  get showLogin$(): Observable<boolean> {
    return this.ctaState$.pipe(map((state) => state === CtaState.Login));
  }

  /**
   * Observable that emits true when the user is logged in (i.e. user is not null) and false otherwise.
   * @returns {Observable<boolean>} - Observable that emits true when the user is logged in and false otherwise.
   */
  get isUserLoggedIn$(): Observable<boolean> {
    return this.user$.pipe(map((user) => !!user));
  }

  /**
   * Returns the current user. If the user is not logged in, it returns null.
   * @returns {User | null} - The current user, or null if the user is not logged in.
   */
  get currentUser(): User | null {
    return this.userSubject.value;
  }
}
